// LAP-733 (GE022) — Connected Accounts settings panel.
//
// All static strings now go through react-i18next (namespace: layout). The
// design is aligned with the rest of the Settings dialog (navy header,
// white card body, accent-blue CTA, slate borders).
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import InfoIcon from '@mui/icons-material/Info'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { makePostRequest, makePatchRequest, makeGetRequest } from 'utils/api'
import AddConnection from './AddConnection'

const NAVY = '#001B42'
const ACCENT = '#2563EB'
const SLATE = '#475569'
const SLATE_BORDER = '#E5E7EB'

export default function ComptesConnectes({ appUser, setAppUser, handleClose, fetchUser }) {
  const { t } = useTranslation('layout')

  const [apiList, setApiList] = useState([])
  const [syncDialogOpen, setSyncDialogOpen] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [syncSettings, setSyncSettings] = useState({
    sync_out_enabled: false,
    sync_out_scope: 'Life_only',
    default_calendar_id: 'primary',
  })

  useEffect(() => {
    refreshList()
  }, [])

  const refreshList = async () => {
    const response = await makeGetRequest('/api/user/account-api/', {}, true)
    if (response.status === 200) setApiList(response.data)
  }

  const { handleSubmit } = useForm({ defaultValues: {} })
  const onSubmit = async () => {
    alert(t('settings.connectedAccounts.syncDialog.savedSuccess'))
  }

  const onApiDel = async (id) => {
    setApiList(apiList.filter((e) => e.id !== id))
    await makePostRequest('/api/user/del-account-api/', { id }, {}, true)
  }

  const handleStatusToggle = async (index) => {
    const next = [...apiList]
    next[index].status = !next[index].status
    setApiList(next)
    await makePostRequest(
      '/api/user/del-account-api/',
      { id: next[index].id, terminate: next[index].status },
      {},
      true,
    )
  }

  const handleReconnect = async (row) => {
    const response = await makePostRequest(
      '/api/user/add-connectes/',
      { type: row.acc_type },
      {},
      true,
    )
    if (response.status === 200) {
      window.location.replace(response.data.url)
    }
  }

  // LAP-614: Sync settings management
  const handleOpenSyncSettings = (account) => {
    setSelectedAccount(account)
    setSyncSettings({
      sync_out_enabled: account.sync_out_enabled || false,
      sync_out_scope: account.sync_out_scope || 'Life_only',
      default_calendar_id: account.default_calendar_id || 'primary',
    })
    setSyncDialogOpen(true)
  }

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false)
    setSelectedAccount(null)
  }

  const handleSaveSyncSettings = async () => {
    if (!selectedAccount) return
    try {
      const response = await makePatchRequest(
        `/api/user/calendar-accounts/${selectedAccount.id}/sync-settings/`,
        syncSettings,
        {},
        true,
      )
      if (response.status === 200) {
        const updatedList = apiList.map((acc) =>
          acc.id === selectedAccount.id ? { ...acc, ...syncSettings } : acc,
        )
        setApiList(updatedList)
        alert(t('settings.connectedAccounts.syncDialog.savedSuccess'))
        handleCloseSyncDialog()
      } else {
        alert(t('settings.connectedAccounts.syncDialog.savedError'))
      }
    } catch (error) {
      console.error('Error updating sync settings:', error)
      alert(t('settings.connectedAccounts.syncDialog.savedError'))
    }
  }

  const isCalendarAccount = (account) =>
    account.acc_type === 'Google' || account.acc_type === 'Microsoft'

  const count = apiList.length

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#F8FAFC' }}>
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #0A2A66 100%)`,
            color: 'white',
            px: { xs: 2, md: 4 },
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <HubOutlinedIcon sx={{ fontSize: 36 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
              {t('settings.connectedAccounts.title')}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              {t('settings.connectedAccounts.subtitle')}
            </Typography>
          </Box>
          <Chip
            label={t('settings.connectedAccounts.count', { count })}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              border: '1px solid rgba(255,255,255,0.25)',
            }}
          />
          <Box>
            <AddConnection updateList={refreshList} />
          </Box>
        </Box>

        {/* Table card */}
        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
          <Card
            variant="outlined"
            sx={{ borderColor: SLATE_BORDER, borderRadius: 2, boxShadow: 'none' }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {apiList.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center', color: SLATE }}>
                  <Typography sx={{ fontSize: 14 }}>
                    {t('settings.connectedAccounts.empty')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.category')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.account')}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.identifier')}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.addedOn')}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.status')}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.syncSettings')}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            color: SLATE,
                            letterSpacing: 0.4,
                          }}
                        >
                          {t('settings.connectedAccounts.columns.remove')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {apiList.map((row, index) => (
                        <TableRow
                          key={row.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ fontSize: 14 }}>{row.categorie}</TableCell>
                          <TableCell sx={{ fontSize: 14 }}>{row.name}</TableCell>
                          <TableCell sx={{ fontSize: 14 }}>{row.user_name}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 13, color: SLATE }}>
                            {row.date_decreation}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={!!row.status}
                              onChange={() => handleStatusToggle(index)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  bgcolor: ACCENT,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isCalendarAccount(row) && row.status && !row.temoin_darchivage ? (
                              <Tooltip
                                title={t('settings.connectedAccounts.actions.configureSync')}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ color: row.sync_out_enabled ? ACCENT : SLATE }}
                                  onClick={() => handleOpenSyncSettings(row)}
                                >
                                  <SettingsIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {t('settings.connectedAccounts.actions.noSync')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.temoin_darchivage ? (
                              <Button
                                onClick={() => handleReconnect(row)}
                                variant="outlined"
                                size="small"
                                color="error"
                                sx={{ textTransform: 'none' }}
                              >
                                {t('settings.connectedAccounts.actions.disconnected')}
                              </Button>
                            ) : (
                              <Tooltip title={t('settings.connectedAccounts.actions.delete')}>
                                <IconButton
                                  size="small"
                                  onClick={() => onApiDel(row.id)}
                                  sx={{ color: SLATE, '&:hover': { color: '#DC2626' } }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            bgcolor: 'white',
            px: { xs: 2, md: 4 },
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                color: SLATE,
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
            >
              {t('settings.connectedAccounts.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                bgcolor: ACCENT,
                px: 3,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
              }}
            >
              {t('settings.connectedAccounts.save')}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* LAP-614: Sync Settings Dialog (now fully localised) */}
      <Dialog
        open={syncDialogOpen}
        onClose={handleCloseSyncDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {t('settings.connectedAccounts.syncDialog.title')}
            {selectedAccount && (
              <Typography variant="caption" color="text.secondary">
                ({selectedAccount.name})
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_out_enabled}
                    onChange={(e) =>
                      setSyncSettings({ ...syncSettings, sync_out_enabled: e.target.checked })
                    }
                  />
                }
                label={t('settings.connectedAccounts.syncDialog.enableLabel')}
              />
              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                <InfoIcon fontSize="small" color="info" />
                <Typography variant="caption" color="text.secondary">
                  {t('settings.connectedAccounts.syncDialog.enableHelp')}
                </Typography>
              </Box>
            </Box>

            <FormControl fullWidth disabled={!syncSettings.sync_out_enabled}>
              <InputLabel>
                {t('settings.connectedAccounts.syncDialog.scopeLabel')}
              </InputLabel>
              <Select
                value={syncSettings.sync_out_scope}
                label={t('settings.connectedAccounts.syncDialog.scopeLabel')}
                onChange={(e) =>
                  setSyncSettings({ ...syncSettings, sync_out_scope: e.target.value })
                }
              >
                <MenuItem value="Life_only">
                  {t('settings.connectedAccounts.syncDialog.scopeLifeOnly')}
                </MenuItem>
                <MenuItem value="All_internal">
                  {t('settings.connectedAccounts.syncDialog.scopeAllInternal')}
                </MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('settings.connectedAccounts.syncDialog.scopeHelp')}
              </Typography>
            </FormControl>

            <TextField
              fullWidth
              label={t('settings.connectedAccounts.syncDialog.targetLabel')}
              value={syncSettings.default_calendar_id}
              onChange={(e) =>
                setSyncSettings({ ...syncSettings, default_calendar_id: e.target.value })
              }
              disabled={!syncSettings.sync_out_enabled}
              helperText={t('settings.connectedAccounts.syncDialog.targetHelp')}
            />

            <Box
              sx={{
                p: 2,
                bgcolor: '#EFF6FF',
                borderRadius: 1,
                display: 'flex',
                gap: 1,
              }}
            >
              <InfoIcon sx={{ color: ACCENT }} />
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {t('settings.connectedAccounts.syncDialog.circularTitle')}
                </Typography>
                <Typography variant="caption">
                  {t('settings.connectedAccounts.syncDialog.circularBody')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncDialog} sx={{ textTransform: 'none', color: SLATE }}>
            {t('settings.connectedAccounts.syncDialog.cancel')}
          </Button>
          <Button
            onClick={handleSaveSyncSettings}
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' },
            }}
          >
            {t('settings.connectedAccounts.syncDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  )
}
